// ────────────────────────────────────────────────────────────
// DEAL PAGE — a thin record viewed against its tract
// ────────────────────────────────────────────────────────────
// The deal carries party ref, status, pricing history, and a Close
// link — nothing title-shaped. Everything below the header is the
// TRACT's chain and worksheet, viewed through the deal lens with
// the subject highlighted. Operations (queue, party actions) live
// on the tract page.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getDealSource, type DealView } from './data/source';
import { ChainView } from './components/ChainView';
import { StickyStrip } from './components/StickyStrip';
import { TitleRunStrip } from './components/TitleRunStrip';
import { WorksheetPanel, MathNotesPanel, FlagsPanel } from './components/Panels';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; view: DealView };

export function DealPage() {
  const { dealId } = useParams<{ dealId: string }>();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  useEffect(() => {
    if (!dealId) return;
    let alive = true;
    setState({ kind: 'loading' });
    getDealSource().getDeal(dealId)
      .then(view => { if (alive) setState({ kind: 'ready', view }); })
      .catch(e => { if (alive) setState({ kind: 'error', message: (e as Error).message }); });
    return () => { alive = false; };
  }, [dealId]);

  const onJumpToDoc = (docNumber: string) => {
    const el = document.getElementById(`dd-doc-${docNumber}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.remove('dd-flash');
    void (el as HTMLElement).offsetWidth;
    el.classList.add('dd-flash');
    window.setTimeout(() => el.classList.remove('dd-flash'), 1800);
  };

  if (state.kind === 'loading') return <div className="dd-loading">Loading deal…</div>;
  if (state.kind === 'error') {
    return (
      <div className="dd-error">
        <strong>Could not load this deal</strong>
        {state.message}
      </div>
    );
  }

  const { deal, tract, lens } = state.view;
  const confBand = tract.confidence >= 70 ? 'high' : tract.confidence >= 45 ? 'mid' : 'low';
  const parts = deal.subjectParty.split(' ');
  const last = parts[parts.length - 1];
  const first = parts.slice(0, -1).join(' ');

  return (
    <div className="dd-page">
      <header className="dd-header">
        <div className="dd-header-main">
          <div className="dd-header-toprow">
            <h1 className="dd-owner-name">{first && <>{first} </>}<em>{last}</em></h1>
            <span className="dd-stage-chip">{deal.stage}</span>
            {deal.closedOutcome && (
              <span className={`dd-badge ${deal.closedOutcome === 'won' ? 'status-verified' : 'not-applied'}`}>
                closed {deal.closedOutcome}{deal.soldTo ? ` · ${deal.soldTo}` : ''}
              </span>
            )}
          </div>
          <div className="dd-header-meta">
            <Link to="/deal-desk" className="dd-back-link">← Deals</Link>
            <span className="sep">·</span>
            <Link to={`/deal-desk/tracts/${encodeURIComponent(tract.trs)}`} className="trs" title="Open the tract — the title source of truth">
              Tract {tract.trs} ↗
            </Link>
            <span className="sep">·</span>
            <span>{deal.county} County, CO</span>
            <span className="sep">·</span>
            <span>{deal.status}</span>
            <span className="sep">·</span>
            <CopyRef refValue={deal.ref} />
            {deal.closeUrl && (
              <>
                <span className="sep">·</span>
                <a href={deal.closeUrl} target="_blank" rel="noreferrer">Close lead ↗</a>
              </>
            )}
          </div>
        </div>

        <div className="dd-header-stats">
          <div>
            <div className="dd-stat-label">NMA · derived from tract</div>
            <div className="dd-stat-nma">{lens.nmaComputed ?? '—'}<span className="unit">net mineral acres</span></div>
            {!tract.terminatesAtPatent && <div className="dd-stat-sub">upper bound · chain incomplete</div>}
            {lens.nmaComputed !== null && lens.nmaStored !== null &&
              Math.abs(lens.nmaComputed - lens.nmaStored) > 0.001 && (
              <div className="dd-stat-sub">⚠ stored {lens.nmaStored}</div>
            )}
          </div>
          <div>
            <div className="dd-stat-label">Confidence</div>
            <div className={`dd-conf ${confBand}`}>{tract.confidence}<span className="pct">%</span></div>
            <div className={`dd-conf-band ${confBand}`}>{confBand === 'low' ? 'LOW' : confBand === 'mid' ? 'MODERATE' : 'HIGH'}</div>
          </div>
        </div>

        {tract.confidenceReasons.length > 0 && (
          <div className="dd-conf-reasons">
            {tract.confidenceReasons.map((r, i) => (
              <div key={i} className="dd-conf-reason">{r}</div>
            ))}
          </div>
        )}
      </header>

      <StickyStrip>
        <span className="who">{deal.subjectParty}</span>
        <span className="sep">·</span>
        <span className="nma">{lens.nmaComputed ?? '—'} NMA</span>
        {!tract.terminatesAtPatent && <span className="tag">upper bound</span>}
        <span className="sep">·</span>
        <span className={`conf ${confBand}`}>{tract.confidence}% {confBand === 'low' ? 'LOW' : confBand === 'mid' ? 'MODERATE' : 'HIGH'}</span>
        <span className="sep">·</span>
        <span className="ref">{deal.ref}</span>
      </StickyStrip>

      <TitleRunStrip scope={{ kind: 'deal', trs: tract.trs, dealId: deal.id }} />

      <div className="dd-columns">
        <div>
          <ChainView
            tract={tract}
            highlightKey={lens.highlightKey}
            subjectLabel={deal.subjectParty}
            subjectNma={lens.nmaComputed}
            spineLinkIds={lens.spineLinkIds}
            carrierKeys={lens.carrierKeys}
            executorKeys={lens.executorKeys}
            subjectTrack={lens.subjectTrack}
          />
        </div>
        <div>
          <WorksheetPanel tract={tract} estatePath={lens.estatePath} />
          <MathNotesPanel tract={tract} />
          <FlagsPanel tract={tract} onJumpToDoc={onJumpToDoc} />
          <div className="dd-card">
            <div className="dd-requests dd-request-note" style={{ borderTop: 'none', marginTop: 0, paddingTop: 12 }}>
              Party actions, the ingest queue, and branch traces live on the{' '}
              <Link to={`/deal-desk/tracts/${encodeURIComponent(tract.trs)}`} style={{ color: 'var(--accent)' }}>
                tract page
              </Link>{' '}— one chain, one source of truth.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CopyRef({ refValue }: { refValue: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    // Copies the file-rename prefix, trailing hyphen included.
    try {
      await navigator.clipboard.writeText(`${refValue}-`);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch { /* clipboard unavailable */ }
  };
  return (
    <button
      className={`dd-copy-ref ${copied ? 'copied' : ''}`}
      onClick={() => void copy()}
      title={copied ? 'Copied' : `Copy "${refValue}-" for manual-inbox filenames`}
    >
      {copied ? '✓ copied' : `${refValue} ⧉`}
    </button>
  );
}
